/** Empty / error / loading states - required for every data component (doc 01 §9). */

import { AlertCircle, Copy, Inbox, RefreshCw, SearchX } from 'lucide-react'
import type { ReactNode } from 'react'

import { ApiError } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/primitives'
import { cn } from '@/lib/cn'

export function EmptyState({
  icon: Icon = Inbox,
  title,
  description,
  action,
  className,
}: {
  icon?: typeof Inbox
  title: string
  description?: string
  action?: ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-2 px-6 py-14 text-center',
        className,
      )}
    >
      <Icon className="size-6 text-[var(--text-muted)]" aria-hidden />
      <p className="text-sm font-medium">{title}</p>
      {description && (
        <p className="max-w-md text-[13px] text-[var(--text-secondary)]">{description}</p>
      )}
      {action && <div className="mt-2">{action}</div>}
    </div>
  )
}

export function FilteredEmptyState({ onClear }: { onClear: () => void }) {
  return (
    <EmptyState
      icon={SearchX}
      title="No results for these filters"
      action={
        <Button size="sm" variant="secondary" onClick={onClear}>
          Clear filters
        </Button>
      }
    />
  )
}

export function ErrorState({
  error,
  onRetry,
  variant = 'inline',
}: {
  error: unknown
  onRetry?: () => void
  variant?: 'inline' | 'page'
}) {
  const apiError = error instanceof ApiError ? error : null
  const message = error instanceof Error ? error.message : 'Unexpected error'
  const traceId = apiError?.traceId

  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-start gap-2 rounded-[var(--radius-card)] p-4',
        'bg-[color-mix(in_oklab,var(--status-critical)_8%,transparent)]',
        'ring-1 ring-[color-mix(in_oklab,var(--status-critical)_30%,transparent)]',
        variant === 'page' && 'mx-auto mt-16 max-w-lg',
      )}
    >
      <div className="flex items-center gap-2">
        <AlertCircle className="size-4 text-[var(--status-critical)]" aria-hidden />
        <p className="text-sm font-medium">{apiError?.problem?.title ?? 'Request failed'}</p>
      </div>
      <p className="text-[13px] text-[var(--text-secondary)]">{message}</p>
      {apiError && apiError.fieldErrors.length > 0 && (
        <ul className="list-inside list-disc text-[13px] text-[var(--text-secondary)]">
          {apiError.fieldErrors.map((fieldError, index) => (
            <li key={`${fieldError.code}-${index}`}>
              {fieldError.field ? `${fieldError.field}: ` : ''}
              {fieldError.message}
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-center gap-2">
        {onRetry && (
          <Button size="sm" variant="secondary" onClick={onRetry}>
            <RefreshCw className="size-3.5" /> Retry
          </Button>
        )}
        {traceId && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => navigator.clipboard?.writeText(traceId)}
            title="Copy the trace id for support"
          >
            <Copy className="size-3.5" /> {traceId}
          </Button>
        )}
      </div>
    </div>
  )
}

export function TableSkeleton({ rows = 6, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <div className="divide-y divide-[var(--border)]">
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="flex items-center gap-4 px-3 py-2.5">
          {Array.from({ length: columns }).map((__, columnIndex) => (
            <Skeleton
              key={columnIndex}
              className="h-3.5"
              style={{ width: columnIndex === 0 ? '22%' : `${Math.max(8, 60 / columns)}%` }}
            />
          ))}
        </div>
      ))}
    </div>
  )
}
